"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";

type FormState = {
  name: string;
  email: string;
  company: string;
  platform: string;
  monthlyOrders: string;
  message: string;
  honeypot: string; // Bot detection
};

type FormErrors = {
  name?: string;
  email?: string;
  company?: string;
  platform?: string;
  monthlyOrders?: string;
};

const initialState: FormState = {
  name: "",
  email: "",
  company: "",
  platform: "",
  monthlyOrders: "",
  message: "",
  honeypot: ""
};

const platforms = ["Shopify", "WooCommerce", "BigCommerce", "TikTok Shop", "Amazon", "Other"];
const orderRanges = ["< 100 orders/month", "100–500/month", "500–2,000/month", "2,000–10,000/month", "10,000+/month"];

export default function ContactForm() {
  const [form, setForm] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case "name":
        if (!value.trim()) return "Full name is required";
        if (value.trim().length < 2) return "Name must be at least 2 characters";
        return undefined;
      case "email":
        if (!value.trim()) return "Email address is required";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return "Please enter a valid email address";
        return undefined;
      case "company":
        if (!value.trim()) return "Company/Brand name is required";
        if (value.trim().length < 2) return "Company name must be at least 2 characters";
        return undefined;
      case "platform":
        if (!value) return "Please select an ecommerce platform";
        return undefined;
      case "monthlyOrders":
        if (!value) return "Please select your monthly order volume";
        return undefined;
      default:
        return undefined;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    
    // Clear API error when user starts typing
    if (apiError) setApiError(null);
    
    // Real-time validation for touched fields
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    (Object.keys(form) as Array<keyof FormState>).forEach((key) => {
      if (key !== "message" && key !== "honeypot") {
        const error = validateField(key, form[key]);
        if (error) {
          newErrors[key as keyof FormErrors] = error;
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    setTouched({
      name: true,
      email: true,
      company: true,
      platform: true,
      monthlyOrders: true,
    });

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setApiError(null);

    try {
      const response = await fetch('/api/book-call', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName: form.name,
          email: form.email,
          company: form.company,
          platform: form.platform,
          monthlyVolume: form.monthlyOrders,
          message: form.message,
          honeypot: form.honeypot
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Submission failed. Please try again.');
      }

      setSubmitted(true);
    } catch (error: any) {
      setApiError(error.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="glass-card-dark rounded-3xl sm:rounded-4xl border border-border-dark p-8 sm:p-12 text-center">
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4 sm:mb-6">
          <Icon name="CheckCircleIcon" size={32} className="text-green-400" />
        </div>
        <h3 className="text-xl sm:text-2xl font-extrabold font-heading text-text-primary mb-3">Request received. We'll respond within 2 hours.</h3>
        <p className="text-sm sm:text-base text-text-secondary max-w-sm mx-auto mb-6">
          Your message has been received. A member of our team will reach out to schedule your free strategy call.
        </p>
        <div className="bg-dark-bg/50 rounded-xl p-4 border border-border-dark">
          <p className="text-xs text-gray-400 mb-2">Submitted details:</p>
          <div className="text-sm text-text-secondary space-y-1">
            <p><span className="text-gray-500">Name:</span> {form.name}</p>
            <p><span className="text-gray-500">Email:</span> {form.email}</p>
            <p><span className="text-gray-500">Company:</span> {form.company}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card-dark rounded-3xl sm:rounded-4xl border border-border-dark p-6 sm:p-8 lg:p-10 shadow-glass hover:shadow-glass-hover transition-shadow duration-300">
      <h3 className="text-lg sm:text-xl font-extrabold font-heading text-text-primary mb-2">Book a Free Strategy Call</h3>
      <p className="text-xs sm:text-sm text-gray-500 mb-6 sm:mb-8">30 minutes · No obligation · Includes free logistics audit</p>

      {apiError && (
        <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-2">
          <Icon name="ExclamationCircleIcon" size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{apiError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5" noValidate>
        {/* Honeypot field - hidden from users, bots will fill it */}
        <input
          type="text"
          name="honeypot"
          value={form.honeypot}
          onChange={handleChange}
          style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
          tabIndex={-1}
          autoComplete="off"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          <div>
            <label className="block text-xs sm:text-sm font-semibold text-text-secondary mb-2 font-heading" htmlFor="name">
              Full Name *
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Alex Johnson"
              className={`w-full px-4 py-3.5 sm:py-3 rounded-xl border bg-dark-bg text-text-primary text-sm focus:outline-none focus:ring-2 transition-all min-h-[48px] ${
                errors.name && touched.name
                  ? "border-red-500 focus:ring-red-500 focus:border-red-500" :"border-border-dark focus:ring-accent-primary focus:border-accent-primary"
              }`}
            />
            {errors.name && touched.name && (
              <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={14} />
                {errors.name}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-semibold text-text-secondary mb-2 font-heading" htmlFor="email">
              Email Address *
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="alex@yourbrand.com"
              className={`w-full px-4 py-3.5 sm:py-3 rounded-xl border bg-dark-bg text-text-primary text-sm focus:outline-none focus:ring-2 transition-all min-h-[48px] ${
                errors.email && touched.email
                  ? "border-red-500 focus:ring-red-500 focus:border-red-500" :"border-border-dark focus:ring-accent-primary focus:border-accent-primary"
              }`}
            />
            {errors.email && touched.email && (
              <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={14} />
                {errors.email}
              </p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-semibold text-text-secondary mb-2 font-heading" htmlFor="company">
            Company / Brand Name *
          </label>
          <input
            id="company"
            name="company"
            type="text"
            value={form.company}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Your Brand Co."
            className={`w-full px-4 py-3.5 sm:py-3 rounded-xl border bg-dark-bg text-text-primary text-sm focus:outline-none focus:ring-2 transition-all min-h-[48px] ${
              errors.company && touched.company
                ? "border-red-500 focus:ring-red-500 focus:border-red-500" :"border-border-dark focus:ring-accent-primary focus:border-accent-primary"
            }`}
          />
          {errors.company && touched.company && (
            <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={14} />
              {errors.company}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          <div>
            <label className="block text-xs sm:text-sm font-semibold text-text-secondary mb-2 font-heading" htmlFor="platform">
              Ecommerce Platform *
            </label>
            <select
              id="platform"
              name="platform"
              value={form.platform}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`w-full px-4 py-3.5 sm:py-3 rounded-xl border bg-dark-bg text-text-primary text-sm focus:outline-none focus:ring-2 transition-all appearance-none min-h-[48px] ${
                errors.platform && touched.platform
                  ? "border-red-500 focus:ring-red-500 focus:border-red-500" :"border-border-dark focus:ring-accent-primary focus:border-accent-primary"
              }`}
            >
              <option value="">Select platform</option>
              {platforms?.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            {errors.platform && touched.platform && (
              <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={14} />
                {errors.platform}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-semibold text-text-secondary mb-2 font-heading" htmlFor="monthlyOrders">
              Monthly Order Volume *
            </label>
            <select
              id="monthlyOrders"
              name="monthlyOrders"
              value={form.monthlyOrders}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`w-full px-4 py-3.5 sm:py-3 rounded-xl border bg-dark-bg text-text-primary text-sm focus:outline-none focus:ring-2 transition-all appearance-none min-h-[48px] ${
                errors.monthlyOrders && touched.monthlyOrders
                  ? "border-red-500 focus:ring-red-500 focus:border-red-500" :"border-border-dark focus:ring-accent-primary focus:border-accent-primary"
              }`}
            >
              <option value="">Select range</option>
              {orderRanges?.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            {errors.monthlyOrders && touched.monthlyOrders && (
              <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={14} />
                {errors.monthlyOrders}
              </p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-semibold text-text-secondary mb-2 font-heading" htmlFor="message">
            Tell us about your business
          </label>
          <textarea
            id="message"
            name="message"
            rows={4}
            value={form.message}
            onChange={handleChange}
            placeholder="What products do you sell? What are your current logistics challenges?"
            className="w-full px-4 py-3.5 sm:py-3 rounded-xl border border-border-dark bg-dark-bg text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-accent-primary transition-all resize-none"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-accent-primary text-white font-bold text-sm sm:text-base transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 min-h-[56px]"
          style={{
            boxShadow: "0 0 40px rgba(91, 46, 255, 0.4)"
          }}
        >
          {loading ? (
            <>
              <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Icon name="PaperAirplaneIcon" size={18} />
              Book Your Free Call
            </>
          )}
        </button>
      </form>
    </div>
  );
}